import { ref, computed } from 'vue'
import { loadToy, generateToysIndex } from '@/utils/toy-registry'
import staticMeta from '@/data/toys-metadata.json'

/**
 * Toy 数据管理组合函数
 */
export function useToyData() {
  const toys = ref([])
  const loading = ref(false)
  const error = ref(null)

  /**
   * 加载所有 Toy 的元数据
   * 优化策略：先使用静态元数据快速显示，然后异步加载动态配置补充
   */
  async function loadAllToys() {
    loading.value = true
    error.value = null

    try {
      // 第一步：优先使用静态元数据，立即显示首页内容（完整数据）
      const byPath = new Map()
      const staticToys = staticMeta?.toys || (Array.isArray(staticMeta) ? staticMeta : [])
      if (staticToys.length > 0) {
        staticToys.forEach(t => {
          const key = t.path || (t.id && t.id.includes('/') ? t.id : (t.category && t.id ? `${t.category}/${t.id}` : t.id))
          if (key) byPath.set(key, { ...t, path: t.path || key })
        })
      }
      toys.value = Array.from(byPath.values())
      loading.value = false // 先显示静态数据（现在包含完整信息）

      // 第二步：异步加载动态配置，在后台补充和更新数据
      // 使用 requestIdleCallback 或 setTimeout 延迟执行，不阻塞首次渲染
      const loadDynamicConfigs = async () => {
        try {
          const dynamic = await generateToysIndex()
          dynamic.forEach(t => {
            const key = t.path || (t.id && t.id.includes('/') ? t.id : (t.category && t.id ? `${t.category}/${t.id}` : t.id))
            if (key) {
              // 合并动态配置，动态配置优先级更高
              // 确保所有字段都被正确合并，包括titleEn和keywords
              const existing = byPath.get(key)
              const merged = { ...existing, ...t, path: t.path || key }
              // 确保关键字段存在（如果动态配置中有，使用动态配置的值）
              if (t.titleEn !== undefined) merged.titleEn = t.titleEn
              if (t.keywords !== undefined) merged.keywords = t.keywords
              if (t.description !== undefined) merged.description = t.description
              byPath.set(key, merged)
            }
          })
          toys.value = Array.from(byPath.values())
          console.log('动态配置加载完成，toy数量:', toys.value.length)
          // 调试：检查linear-regression的数据
          const linearToy = toys.value.find(t => t.id === 'linear-regression')
          if (linearToy) {
            console.log('linear-regression数据:', {
              id: linearToy.id,
              titleEn: linearToy.titleEn,
              keywords: linearToy.keywords
            })
          }
        } catch (err) {
          console.warn('Failed to load dynamic toy configs:', err)
          // 不影响用户体验，静默失败
        }
      }

      // 延迟加载动态配置，优先保证首次渲染速度
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadDynamicConfigs, { timeout: 2000 })
      } else {
        setTimeout(loadDynamicConfigs, 100)
      }
    } catch (err) {
      error.value = err.message
      console.error('Failed to load toys:', err)
      loading.value = false
    }
  }

  /**
   * 按分类过滤 Toy
   */
  function filterByCategory(category) {
    return computed(() => 
      toys.value.filter(toy => toy.category === category)
    )
  }

  /**
   * 按难度过滤 Toy
   */
  function filterByDifficulty(difficulty) {
    return computed(() => 
      toys.value.filter(toy => toy.difficulty === difficulty)
    )
  }

  /**
   * 根据 ID 获取 Toy
   */
  function getToyById(id) {
    return computed(() => 
      toys.value.find(toy => toy.id === id)
    )
  }

  return {
    toys,
    loading,
    error,
    loadAllToys,
    loadToy,
    filterByCategory,
    filterByDifficulty,
    getToyById
  }
}
